import Immutable from 'immutable';
import axios from 'axios';
import store from 'store2';
import config from '../../config';

// ─── Action types ────────────────────────────────────────────────────────────

const UPLOAD         = 'dataFile/UPLOAD';
const UPLOAD_SUCCESS = 'dataFile/UPLOAD_SUCCESS';
const UPLOAD_FAIL    = 'dataFile/UPLOAD_FAIL';
const UPLOAD_RESET   = 'dataFile/UPLOAD_RESET';

const FETCH          = 'dataFile/FETCH';
const FETCH_SUCCESS  = 'dataFile/FETCH_SUCCESS';
const FETCH_FAIL     = 'dataFile/FETCH_FAIL';

// ─── Initial state ────────────────────────────────────────────────────────────

const initialState = Immutable.fromJS({
    isUploading: false,
    uploadResult: null,   // { success, message, key, overwritten }
    uploadError: null,

    isFetching: false,
    fetchedFileUrl: null, // Object URL for the downloaded CSV blob
    fetchError: null,
});

// ─── Reducer ─────────────────────────────────────────────────────────────────

export default function reducer(state = initialState, action) {
    switch (action.type) {
        case UPLOAD:
            return state
                .set('isUploading', true)
                .set('uploadResult', null)
                .set('uploadError', null);

        case UPLOAD_SUCCESS:
            return state
                .set('isUploading', false)
                .set('uploadResult', Immutable.fromJS(action.result));

        case UPLOAD_FAIL:
            return state
                .set('isUploading', false)
                .set('uploadError', action.error);

        case UPLOAD_RESET:
            return state
                .set('uploadResult', null)
                .set('uploadError', null);

        case FETCH:
            return state
                .set('isFetching', true)
                .set('fetchedFileUrl', null)
                .set('fetchError', null);

        case FETCH_SUCCESS:
            return state
                .set('isFetching', false)
                .set('fetchedFileUrl', action.fileUrl);

        case FETCH_FAIL:
            return state
                .set('isFetching', false)
                .set('fetchError', action.error);

        default:
            return state;
    }
}

// ─── Action creators ─────────────────────────────────────────────────────────

/**
 * Upload a CSV file to S3.
 * @param {{ dataset: string, year: string, month: string, file: File }} params
 */
export const uploadDataFile = ({ dataset, year, month, file }) => async (dispatch) => {
    dispatch({ type: UPLOAD });
    try {
        const formData = new FormData();
        formData.append('dataset', dataset);
        formData.append('year', year);
        formData.append('month', month);
        formData.append('file', file);

        const token = store('authToken');
        const { data } = await axios.post(
            `${config.apiHost}/data-files/upload`,
            formData,
            {
                headers: {
                    ...(token ? { Authorization: `bearer ${token}` } : {}),
                    // Content-Type is set automatically by axios for FormData
                },
            }
        );
        dispatch({ type: UPLOAD_SUCCESS, result: data });
    } catch (err) {
        const message =
            (err.response && err.response.data && err.response.data.message) ||
            err.message ||
            'Upload failed';
        dispatch({ type: UPLOAD_FAIL, error: message });
    }
};

export const resetUpload = () => ({ type: UPLOAD_RESET });

/**
 * Fetch a CSV file from S3 and create a local object URL for downloading/previewing.
 * @param {{ dataset: string, year: string, month: string }} params
 */
export const fetchDataFile = ({ dataset, year, month }) => async (dispatch) => {
    dispatch({ type: FETCH });
    try {
        const token = store('authToken');
        const response = await axios.get(
            `${config.apiHost}/data-files/file/${year}/${month}/${dataset}`,
            {
                responseType: 'blob',
                headers: token ? { Authorization: `bearer ${token}` } : {},
            }
        );

        const blobUrl = URL.createObjectURL(
            new Blob([response.data], { type: 'text/csv' })
        );
        dispatch({ type: FETCH_SUCCESS, fileUrl: blobUrl });
    } catch (err) {
        const message =
            (err.response && err.response.data && err.response.data.message) ||
            err.message ||
            'File not found';
        dispatch({ type: FETCH_FAIL, error: message });
    }
};
